import { Loader, NumberInput, Select, Switch, Table } from "@mantine/core";
import { IconCheck } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { assistantApi } from "#/api/assistantApi";
import ActionButton from "#/components/buttons/ActionButton";
import {
  showErrorNotification,
  showSuccessNotification,
} from "#/components/notifications";
import BaseTable from "#/components/tables/BaseTable";
import type {
  AssistantModel,
  AssistantModelPricing,
  AssistantUsageRow,
  UpdateClientAssistantPayload,
} from "#/types/assistant";

const DEFAULT_MODEL_VALUE = "__default__";

const formatUsd = (value: number): string =>
  value >= 0.01 ? `$${value.toFixed(2)}` : `$${value.toFixed(4)}`;

const modelOptionLabel = (m: AssistantModelPricing): string =>
  `${m.label} — $${m.inputPerMTok}/$${m.outputPerMTok} per 1M`;

const AssistantTab = ({ clientId }: { clientId: number }) => {
  const queryClient = useQueryClient();
  const overviewKey = ["assistant-overview", clientId];

  const { data: overview, isPending } = useQuery({
    queryKey: overviewKey,
    queryFn: async () => (await assistantApi.getClientOverview(clientId)).data,
  });

  const { data: modelsData } = useQuery({
    queryKey: ["assistant-models"],
    queryFn: async () => (await assistantApi.getModels()).data,
    staleTime: 60 * 60 * 1000,
  });

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: overviewKey });

  const clientMutation = useMutation({
    mutationFn: (body: UpdateClientAssistantPayload) =>
      assistantApi.updateClientSetting(clientId, body),
    onSuccess: () => {
      showSuccessNotification({ message: "Assistant settings updated." });
      invalidate();
    },
    onError: () =>
      showErrorNotification({ message: "Failed to update settings." }),
  });

  if (isPending || !overview) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Loader color="var(--client-color)" />
      </div>
    );
  }

  const models = modelsData?.models ?? [];
  const modelValue = overview.setting.model ?? DEFAULT_MODEL_VALUE;
  const defaultModelLabel =
    models.find((m) => m.model === modelsData?.defaultModel)?.label ??
    "default";

  const headers = [
    "User",
    "Enabled",
    "Daily limit",
    "Messages",
    "Cost",
    "",
  ];

  const rows = overview.users.map((user) => (
    <UserRow key={user.id} user={user} onSaved={invalidate} />
  ));

  return (
    <div className="flex flex-col gap-6 pb-6">
      {/* Client-level controls */}
      <div className="rounded-xl border border-gray-200 bg-white p-5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-slate-700">AI Assistant</p>
            <p className="text-xs text-slate-500">
              Let this client&apos;s users ask questions about their own data.
            </p>
          </div>
          <Switch
            checked={overview.setting.enabled}
            onChange={(event) =>
              clientMutation.mutate({ enabled: event.currentTarget.checked })
            }
            label={overview.setting.enabled ? "Enabled" : "Disabled"}
          />
        </div>

        <div className="mt-5 max-w-md">
          <Select
            label="Model"
            description={`When set to default, this client uses ${defaultModelLabel}.`}
            data={[
              { value: DEFAULT_MODEL_VALUE, label: `Default (${defaultModelLabel})` },
              ...models.map((m) => ({
                value: m.model,
                label: modelOptionLabel(m),
              })),
            ]}
            value={modelValue}
            allowDeselect={false}
            onChange={(value) =>
              clientMutation.mutate({
                model:
                  value === DEFAULT_MODEL_VALUE
                    ? null
                    : (value as AssistantModel),
              })
            }
          />
          {models.map(
            (m) =>
              m.model === overview.setting.effectiveModel && (
                <p key={m.model} className="mt-2 text-xs text-slate-500">
                  {m.description}
                </p>
              )
          )}
        </div>
      </div>

      {/* Usage totals */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <StatCard
          label="Total messages"
          value={overview.totals.messageCount.toLocaleString()}
        />
        <StatCard label="Total cost" value={formatUsd(overview.totals.costUsd)} />
      </div>

      {/* Per-user usage & controls */}
      <div>
        <p className="mb-2 text-sm font-semibold text-slate-700">
          Per-user usage
        </p>
        <BaseTable headers={headers} rows={rows} />
      </div>
    </div>
  );
};

const StatCard = ({ label, value }: { label: string; value: string }) => (
  <div className="rounded-xl border border-gray-200 bg-gradient-to-br from-client-color/5 to-client-color/10 p-5">
    <p className="text-xs text-slate-500">{label}</p>
    <p className="mt-1 text-2xl font-bold text-slate-800">{value}</p>
  </div>
);

const UserRow = ({
  user,
  onSaved,
}: {
  user: AssistantUsageRow;
  onSaved: () => void;
}) => {
  const [limit, setLimit] = useState<number>(user.dailyMessageLimit);

  useEffect(() => {
    setLimit(user.dailyMessageLimit);
  }, [user.dailyMessageLimit]);

  const mutation = useMutation({
    mutationFn: (body: { enabled?: boolean; dailyMessageLimit?: number }) =>
      assistantApi.updateUserSetting(user.id, body),
    onSuccess: () => {
      showSuccessNotification({ message: `Updated ${user.name}.` });
      onSaved();
    },
    onError: () =>
      showErrorNotification({ message: "Failed to update user." }),
  });

  const limitChanged = limit !== user.dailyMessageLimit;

  return (
    <Table.Tr>
      <Table.Td>
        <div className="flex flex-col">
          <span className="font-medium text-slate-700">{user.name}</span>
          <span className="text-xs text-slate-400">{user.email}</span>
        </div>
      </Table.Td>
      <Table.Td>
        <Switch
          checked={user.enabled}
          onChange={(event) =>
            mutation.mutate({ enabled: event.currentTarget.checked })
          }
        />
      </Table.Td>
      <Table.Td>
        <NumberInput
          className="w-24"
          size="xs"
          min={0}
          max={10000}
          value={limit}
          onChange={(value) => setLimit(Number(value) || 0)}
        />
      </Table.Td>
      <Table.Td>{user.messageCount.toLocaleString()}</Table.Td>
      <Table.Td>{formatUsd(user.costUsd)}</Table.Td>
      <Table.Td>
        {limitChanged && (
          <ActionButton
            icon={IconCheck}
            aria-label="Save daily limit"
            loading={mutation.isPending}
            onClick={() => mutation.mutate({ dailyMessageLimit: limit })}
          />
        )}
      </Table.Td>
    </Table.Tr>
  );
};

export default AssistantTab;
